import React, { useCallback, useState } from 'react';
import { Grid, ConfigContext, http } from '../../base-ui/lib';
import { useNavigate } from 'react-router-dom';
import MaterialGridToolbar from './MaterialGridToolbar';
import EmptySearchState from '../../base-ui/Grid/components/EmptySearchState';
import { TextField, IconButton, Box } from '@mui/material';
import {
	Edit as EditIcon,
	SaveOutlined as SaveIcon,
} from '@mui/icons-material';
import { toastError, toastSuccess } from '../../Toaster/Toast';

const EmptyStateComponent = () => <EmptySearchState />;

function MaterialPage() {
	const { config } = React.useContext(ConfigContext);
	const navigate = useNavigate();
	const gridRef = React.useRef(null);

	const [filter, setFilter] = useState('');
	const [allData, setAllData] = useState([]);
	const [editingRowIds, setEditingRowIds] = useState(new Set());
	const [rowChanges, setRowChanges] = useState({});

	const filterRef = React.useRef(filter);
	filterRef.current = filter;

	const renderToolbar = useCallback(
		() => (
			<MaterialGridToolbar
				filter={filterRef.current}
				setFilter={setFilter}
				onAdd={null}
			/>
		),
		[setFilter]
	);

	const handleEditToggle = (row) => {
		setEditingRowIds((prev) => {
			const next = new Set(prev);
			if (next.has(row._id)) {
				next.delete(row._id);
			} else {
				next.add(row._id);
				setRowChanges((prevChanges) => ({
					...prevChanges,
					[row._id]: row.price,
				}));
			}
			return new Set(next);
		});
	};

	const handleCancelEdit = (id) => {
		setEditingRowIds((prev) => {
			const next = new Set(prev);
			next.delete(id);
			return new Set(next);
		});
		setRowChanges((prev) => {
			const next = { ...prev };
			delete next[id];
			return next;
		});
	};

	const handlePriceChange = (id, value) => {
		setRowChanges((prev) => ({
			...prev,
			[id]: value,
		}));
	};

	const handleSaveRow = async (row) => {
		const newPrice = rowChanges[row._id];
		if (newPrice !== undefined && parseFloat(newPrice) < 0.01) {
			return toastError({
				title: 'Invalid Price',
				description: 'Price must be at least $0.01',
			});
		}
		try {
			const response = await http.put(
				{ ...row, price: parseFloat(newPrice) },
				`${config.uri.base}/material/${row._id}`
			);
			if (response.errored) {
				return toastError({
					title: 'Error',
					description: response.error.message,
				});
			}
			toastSuccess({
				title: 'Success',
				description: 'Price updated successfully',
			});

			gridRef.current?.updateData(row._id, { price: parseFloat(newPrice) });
			setEditingRowIds((prev) => {
				const next = new Set(prev);
				next.delete(row._id);
				return new Set(next);
			});
		} catch (error) {
			toastError({
				title: 'Error',
				description: 'Failed to update price',
			});
		}
	};

	const headCells = [
		{
			field: 'obid',
			headerName: 'OBID',
			flex: 0.7,
		},
		{
			field: 'name',
			headerName: 'Name',
			flex: 2,
		},
		{
			field: 'calculationType',
			headerName: 'Calculation',
			flex: 0.8,
			renderCell: (params) => params.value || '\u2014',
		},
		{
			field: 'price',
			headerName: 'Price',
			type: 'number',
			flex: 0.8,
			minWidth: 120,
			sortable: true,
			sortingOrder: ['asc', 'desc'],
			renderCell: (params) => {
				const isEditing = editingRowIds.has(params.row._id);
				if (isEditing) {
					return (
						<TextField
							size="small"
							type="number"
							inputProps={{ min: 0.01, step: 0.01 }}
							value={rowChanges[params.row._id] ?? params.row.price}
							onChange={(e) =>
								handlePriceChange(params.row._id, e.target.value)
							}
							onKeyDown={(e) => {
								if (e.key === 'Enter') {
									handleSaveRow(params.row);
								} else if (e.key === 'Escape') {
									handleCancelEdit(params.row._id);
								}
							}}
							fullWidth
							autoFocus
							variant="outlined"
							slotProps={{
								input: {
									startAdornment: (
										<Box sx={{ mr: 0.5, color: '#263238', fontWeight: 500 }}>
											$
										</Box>
									),
								},
							}}
							sx={{
								'& .MuiOutlinedInput-root': {
									backgroundColor: 'transparent',
									height: '38px',
									color: '#263238',
									fontWeight: 500,
									fontSize: '1rem',
									'& fieldset': {
										borderColor: '#4caf50',
										borderWidth: '1px',
									},
									'&:hover fieldset': {
										borderColor: '#4caf50',
									},
									'&.Mui-focused fieldset': {
										borderColor: '#4caf50',
										borderWidth: '1px',
									},
								},
								'& .MuiInputBase-input': {
									padding: '8px 4px',
								},
							}}
						/>
					);
				}
				const numValue = Number(params.value);
				if (isNaN(numValue) || params.value == null) return '$0.00';
				return numValue.toLocaleString('en-US', {
					style: 'currency',
					currency: 'USD',
				});
			},
		},
		{
			field: 'actions',
			headerName: 'Actions',
			flex: 0.5,
			minWidth: 80,
			sortable: false,
			renderCell: (params) => {
				const isEditing = editingRowIds.has(params.row._id);
				return (
					<Box
						sx={{
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
							width: '100%',
							gap: 1,
						}}
					>
						<IconButton
							size="small"
							onClick={(e) => {
								e.stopPropagation();
								if (isEditing) {
									handleSaveRow(params.row);
								} else {
									handleEditToggle(params.row);
								}
							}}
							sx={
								isEditing
									? {
											backgroundColor: '#e8f5e9',
											color: '#2e7d32',
											borderRadius: '4px',
											'&:hover': {
												backgroundColor: '#c8e6c9',
											},
										}
									: {
											color: 'text.secondary',
										}
							}
						>
							{isEditing ? (
								<SaveIcon fontSize="small" />
							) : (
								<EditIcon fontSize="small" />
							)}
						</IconButton>
					</Box>
				);
			},
		},
	];

	const filterList = [
		{ id: 'obid', label: 'OBID', dataType: 'string', defaultValue: '' },
		{ id: 'name', label: 'Name', dataType: 'string', defaultValue: '' },
		{
			id: 'calculationType',
			label: 'Calculation',
			dataType: 'string',
			defaultValue: '',
		},
		{ id: 'price', label: 'Price', dataType: 'string', defaultValue: '' },
	];

	return (
		<Grid
			ref={gridRef}
			headCells={headCells}
			filterList={filterList}
			pageSize={25}
			pageSizeOptions={[10, 25, 50]}
			searchEndpont={`${config.uri.base}/material`}
			hybridMode={true}
			initialPageSize={5000}
			disableVirtualization={true}
			sx={{
				'& .MuiDataGrid-overlayWrapper': {
					transition: 'none !important',
					animation: 'none !important',
				},
				'& .MuiDataGrid-loadingOverlay': {
					transition: 'none !important',
					animation: 'none !important',
				},
				'& .MuiDataGrid-virtualScroller': {
					transition: 'none !important',
				},
				'& .MuiDataGrid-main': {
					transition: 'none !important',
				},
				'& .MuiDataGrid-row': {
					transition: 'none !important',
				},
			}}
			toolbar={renderToolbar}
			showToolbar
			onDataLoad={setAllData}
			options={{ filter }}
			customNoRowsOverlay={EmptyStateComponent}
		/>
	);
}

export default MaterialPage;
